/**
 * Settings hub — tabbed container for Company + Integrations settings.
 *
 * SPEC §8.1: Settings lives under Admin with Company, Integrations,
 * and other subsections. Users/Automations/Templates already have
 * their own routes; this hub covers the remaining two.
 */
import { useState } from 'react';
import { Building2, Plug } from 'lucide-react';

import { SettingsCompany } from './SettingsCompany.js';
import { SettingsIntegrations } from './SettingsIntegrations.js';

const TABS = [
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'integrations', label: 'Integrations', icon: Plug },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function SettingsHub() {
  const [tab, setTab] = useState<TabId>('company');

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <header className="px-6 py-4 border-b border-ink-600 shrink-0">
        <h1 className="text-lg font-semibold text-ink-100">Settings</h1>
      </header>

      <nav
        className="px-6 border-b border-ink-600 flex gap-0.5 shrink-0"
        role="tablist"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              className={
                'flex items-center gap-1.5 px-3 py-2.5 text-[13px] border-b-2 -mb-px transition-colors focus-ring rounded-t ' +
                (tab === t.id
                  ? 'border-beacon-400 text-ink-100 font-medium'
                  : 'border-transparent text-ink-300 hover:text-ink-100')
              }
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="flex-1 overflow-auto">
        {tab === 'company' && <SettingsCompany />}
        {tab === 'integrations' && <SettingsIntegrations />}
      </div>
    </div>
  );
}
